/**
 * haptic_controller.js — Hotwire Native "haptic" bridge component (web side).
 *
 * Identifier: bridge--haptic (glob-derived from the bridge/ subdir; wire with
 * data-controller="bridge--haptic"). static component = "haptic" MUST equal the
 * iOS class var name and the Android-reached method name.
 *
 * Capability tier: this is NOT a passive Strada-only controller. It extends the
 * Strada BridgeComponent (so it gets `this.send` and only connects inside the
 * iOS Hotwire Native shell), and on connect it contributes the `haptic`
 * implementation triple to the window.ChippieBridge facade:
 *
 *   - ios:     send a "tap" event over the Strada bridge to HapticComponent.swift
 *   - android: call window.ChippieNative.haptic() (no Strada on Android)
 *   - pwa:     navigator.vibrate(10)
 *
 * The facade (chippie-bridge.js) owns transport detection and picks the right
 * impl per call, so callers just do `window.ChippieBridge.haptic(style)`.
 *
 * IMPORTANT: the iOS impl needs a live bridge instance to call `this.send`, which
 * only exists while this controller is connected (i.e. inside the iOS shell). On
 * Android and PWA this controller never connects, but the facade falls back to a
 * standalone registration (see registerStandalone) so haptic() still works there
 * even with no <element data-controller> on the page.
 *
 * Styles: light | medium | heavy | success | warning | error. Unknown/absent ->
 * the native side defaults to medium. The PWA fallback is a flat 10ms buzz
 * (navigator.vibrate has no notion of style).
 */
import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// Android + PWA impls are platform-static (no per-instance state), so register
// them once at module load. The facade is the SSOT for which one runs.
function registerStandalone() {
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("haptic", {
        // ios is overridden by the controller's connect() with a real send; this
        // placeholder is a safe no-op if somehow selected before connect.
        ios: () => {},
        android: () => {
            if (window.ChippieNative && window.ChippieNative.haptic) {
                try { window.ChippieNative.haptic(); } catch (_e) { /* bridge gone */ }
            }
        },
        pwa: (_style) => {
            if (navigator.vibrate) navigator.vibrate(10);
        },
    });
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
    registerStandalone();
} else {
    document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
    static component = "haptic";

    connect() {
        super.connect();
        // Inside the iOS shell now: wire the real Strada send into the facade,
        // keeping the Android/PWA fallbacks intact.
        if (!window.ChippieBridge || !window.ChippieBridge.register) return;
        window.ChippieBridge.register("haptic", {
            ios: (style) => this.send("tap", { style: style || "medium" }),
            android: () => {
                if (window.ChippieNative && window.ChippieNative.haptic) {
                    try { window.ChippieNative.haptic(); } catch (_e) { /* bridge gone */ }
                }
            },
            pwa: (_style) => {
                if (navigator.vibrate) navigator.vibrate(10);
            },
        });
    }

    disconnect() {
        // Leaving the iOS shell context: drop the live-send impl back to the
        // standalone placeholder so a stale `this.send` is never invoked.
        registerStandalone();
        super.disconnect();
    }
}
